import moment from 'moment';
import 'moment-timezone';

import {
    RANGE_LABEL_DAY,
    RANGE_LABEL_MONTH,
    RANGE_LABEL_3_YEARS,
    SENSOR_GRAPH_RANGES,
    EXTENDED_SENSOR_GRAPH_RANGES,
    TEMPERATURE_GRAPH_FILTERS,
    SENSOR_GRAPH_RANGE_STATE_KEY,
    isPetGlycolFilterAvailable,
} from 'services/charting/constants';
import { getHoursMinutesFormat } from 'services/utils';
import ExtendedStorageInfoController from '../../../../common/extended-storage-info/controller';
import ExtendedStorageInfoTemplate from '../../../../common/extended-storage-info/template.html';

export const SENSOR_GRAPH_FILTER = 'sensor:filter'


/* @ngInject */
export default class SensorChartController {
    constructor(StateService, $rootScope, DialogService, FeatureFlags, AnalyticsService, ProjectManager, VirtualGlycolBufferTemperatureVisibilityService) {
        this.StateService = StateService;
        this.$rootScope = $rootScope;
        this.DialogService = DialogService;
        this.FeatureFlags = FeatureFlags;
        this.AnalyticsService = AnalyticsService;
        this.ProjectManager = ProjectManager;
        this.VirtualGlycolBufferTemperatureVisibilityService = VirtualGlycolBufferTemperatureVisibilityService;

        this.featureFlagsLoaded = false;
        this.FeatureFlags.onLoaded().then(() => {
            this.featureFlagsLoaded = true;
            this.applyAvailableFilters();
            this.$rootScope.$applyAsync();
        })

        this.chartBucketSizeSeconds = null;
        this.currentExtremes = null;

        // Redraw when the VGB profile setting changes
        this.unwatchVirtualGlycolBuffer = this.$rootScope.$watch(
            () => this.showsVirtualGlycolBuffer,
            (shows, previous) => {
                if (shows === previous) {
                    return;
                }
                const previousFilter = this.filter;
                this.applyAvailableFilters();
                if (this.filter !== previousFilter) {
                    this.reloadChart();
                }
            }
        );
    }

    $onDestroy() {
        this.unwatchVirtualGlycolBuffer();
    }

    // VGB follows the profile setting, not the dropdown
    get showsVirtualGlycolBuffer() {
        return this.FeatureFlags.isActive('sensor_virtual_glycol_buffer')
            && Boolean(this.thing?.hasVirtualGlycolBufferData)
            && this.VirtualGlycolBufferTemperatureVisibilityService.showTemperatures;
    }

    get hasLongTermStorageFeatureFlag() {
        return this.FeatureFlags.isActive('sensor_long_term_storage')
    }

    get availableRanges() {
        if (this.hasLongTermStorageFeatureFlag) {
            return SENSOR_GRAPH_RANGES;
        }

        return SENSOR_GRAPH_RANGES.filter(range => !EXTENDED_SENSOR_GRAPH_RANGES.includes(range));
    }

    get unavailableRanges() {
        if (this.hasLongTermStorageFeatureFlag) {
            return [];
        }

        return EXTENDED_SENSOR_GRAPH_RANGES;
    }

    get greatestRangeLabel() {
        return this.hasLongTermStorageFeatureFlag ? RANGE_LABEL_3_YEARS : RANGE_LABEL_MONTH;
    }

    get aggregationInfoTooltip() {
        if (this.chartBucketSizeSeconds === null) {
            return '';
        }

        return `Showing average, min, and max per ${this.chartBucketSizeSeconds / 3600}h`;
    }

    get shouldShowAggregationTooltip() {
        if (this.thing.type === 'motion' || this.thing.type === 'deskOccupancy' || this.thing.type === 'touch' || this.thing.type === 'proximity' || this.thing.type === 'contact') {
            return false
        }

        return this.chartBucketSizeSeconds !== null
    }

    // Handling for zooming and setting durations
    handleChartSelection(event) {
        this.currentExtremes = event?.extremes;

        // Set the range label (eg. "Month", "Day", etc.)
        this.currentRange = null;
    }

    // Handling for using Highchart's navigator to pan the chart
    // Used to connect sensors with multiple independent charts
    handleChartPan(event) {
        this.currentExtremes = event?.extremes;
    }

    setInitialized() {
        this.dataLoaded = true;
        this.$rootScope.$applyAsync()
    }

    // Dropdown lists flagged filters only. PET is hidden once VGB is on,
    // and VGB itself is never listed.
    applyAvailableFilters() {
        this.filters = Object.values(TEMPERATURE_GRAPH_FILTERS)
            .filter(filter => filter.value !== 'VIRTUAL_GLYCOL_BUFFER')
            .filter(filter => this.FeatureFlags.isActive(filter.featureFlag))
            .filter(filter => filter.value !== 'GLYCOL' || isPetGlycolFilterAvailable(this.FeatureFlags));
        this.filter = this.showsVirtualGlycolBuffer
            ? TEMPERATURE_GRAPH_FILTERS.VIRTUAL_GLYCOL_BUFFER
            : this.filters.find(filter => filter.value === this.filterValue) || TEMPERATURE_GRAPH_FILTERS.NONE;
    }

    get hasAdvancedFilters() {
        return (this.filters || []).some(filter => filter.value !== 'NONE');
    }

    setFilter(filter) {
        this.filter = filter
        this.StateService.setItem(SENSOR_GRAPH_FILTER, this.filter.value);
        this.reloadChart();
    }

    reloadChart() {
        this.dataLoaded = false
        this.replaceChart = true

        // When replacing the chart after a custom zoom (currentRange is null),
        // reset to the default range to avoid passing null to setRange which
        // causes RANGE_DURATIONS[null] → undefined → NaN extremes → "Invalid date" API error.
        if (this.currentRange === null) {
            this.currentRange = RANGE_LABEL_DAY;
        }

        setTimeout(() => {
            this.replaceChart = false
            this.hideFilterDropdown()
            this.$rootScope.$applyAsync()
        }, 1);
    }

    setChartMetadata(metadata) {
        this.chartBucketSizeSeconds = metadata?.bucketSizeSeconds;
    }

    hideFilterDropdown() {
        this.showFilterDropdown = false
    }

    setRange(range) {
        this.currentRange = range;
        this.currentExtremes = null;
        this.StateService.setItem(SENSOR_GRAPH_RANGE_STATE_KEY, range);
    }

    hideRangeDropdown() {
        this.showRangeDropdown = false
    }

    $onInit() {
        this.dataLoaded = false;

        this.replaceChart = false
        this.showFilterDropdown = false
        this.showRangeDropdown = false
        
        this.filterValue = this.StateService.getItem(SENSOR_GRAPH_FILTER, 'NONE')
        this.applyAvailableFilters()
        
        this.ranges = SENSOR_GRAPH_RANGES;
        this.currentRange = this.StateService.getItem(SENSOR_GRAPH_RANGE_STATE_KEY, RANGE_LABEL_DAY);
            
        this.projectTimeZone = this.ProjectManager.currentProject.location.timeLocation
        this.equalToLocalTimeZone = true

        this.updateLocalTimestamp();
        setInterval(() => {
            this.updateLocalTimestamp();
        }, 1000);
    }

    updateLocalTimestamp() {
        if (this.projectTimeZone !== "") {
            this.localTimestamp = moment().tz(this.projectTimeZone).format(getHoursMinutesFormat());
            this.timezoneDifferenceText = this.formatTimezoneDifference(this.projectTimeZone);
            this.$rootScope.$applyAsync();
        }
    }

    // Function to format the timezone difference
    formatTimezoneDifference(projectTimeZone) {
        const localTimezone = moment.tz.guess();
        const projectTime = moment().tz(projectTimeZone);
        const localTime = moment().tz(localTimezone);

        // Calculate the difference in hours
        const diffHours = projectTime.utcOffset() - localTime.utcOffset();
        const diffInHours = diffHours / 60;  // Convert minutes to hours

        // Determine the text to show based on the time difference
        let timeDiffText = '';
        if (diffInHours > 0) {
            timeDiffText = `${diffInHours} hours ahead`;
            this.equalToLocalTimeZone = false
        } else if (diffInHours < 0) {
            timeDiffText = `${Math.abs(diffInHours)} hours behind`;
            this.equalToLocalTimeZone = false
        } else {
            this.equalToLocalTimeZone = true
        }

        return timeDiffText;
    }

    showExtendedStorageInfo() {
        this.AnalyticsService.trackEvent("device_chart.extended_storage_info_opened")

        this.DialogService.show({
            controller: ExtendedStorageInfoController,
            controllerAs: '$ctrl',
            template: ExtendedStorageInfoTemplate,
            parent: document.body,
            clickOutsideToClose: true,
        });
    }
}
